import { useContext, createContext, useState, useCallback, useEffect, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import axios from 'axios'
import { API_URL } from '../constant/api'

const AuthContext = createContext(null)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuthContext must be used within an AuthContextProvider')
  return context
}

// ── Axios instance ────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// ── Token helpers ─────────────────────────────────────────────────────────────
const isTokenExpired = (decoded) => {
  if (!decoded?.exp) return false
  return decoded.exp < Math.floor(Date.now() / 1000)
}

const saveToStorage = (token) => {
  localStorage.setItem('tokens',      token)
  localStorage.setItem('isLoggedIns', JSON.stringify(true))
}

const clearStorage = () => {
  localStorage.removeItem('tokens')
  localStorage.removeItem('isLoggedIns')
}

// ── Provider ──────────────────────────────────────────────────────────────────
export const AuthContextProvider = ({ children }) => {
  const [token,     setToken]     = useState(null)
  const [authUser,  setAuthUser]  = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const visibilityRef = useRef(document.visibilityState)

  const clearSession = useCallback(() => {
    clearStorage()
    setAuthUser(null)
    setToken(null)
  }, [])

  // ── Load & validate token from localStorage ───────────────────────────────
  const getData = useCallback(() => {
    try {
      const userData   = localStorage.getItem('tokens')
      const isLoggedIn = localStorage.getItem('isLoggedIns')

      if (!userData || !isLoggedIn) {
        setAuthUser(null)
        setToken(null)
        return
      }

      let decoded
      try {
        decoded = jwtDecode(userData)
      } catch {
        clearSession()
        return
      }

      if (isTokenExpired(decoded)) {
        console.warn('Token expired — clearing session')
        clearSession()
        return
      }

      setAuthUser(decoded)
      setToken(userData)
    } catch (error) {
      console.error('Error loading auth data:', error)
      setAuthUser(null)
      setToken(null)
    } finally {
      setIsLoading(false)
    }
  }, [clearSession])

  // ── Re-check expiry when tab becomes visible again ────────────────────────
  useEffect(() => {
    getData()

    const handleVisibilityChange = () => {
      if (visibilityRef.current === 'hidden' && document.visibilityState === 'visible') {
        // Tab re-focused — re-validate token (mirrors AppState foreground check)
        getData()
      }
      visibilityRef.current = document.visibilityState
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [getData])

  // ── Attach token to every axios request ───────────────────────────────────
  useEffect(() => {
    const reqInterceptor = api.interceptors.request.use((config) => {
      if (token) config.headers.Authorization = `Bearer ${token}`
      return config
    })

    // Auto-logout on 401 from any API call
    const resInterceptor = api.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('401 received — clearing session')
          clearSession()
        }
        return Promise.reject(error)
      }
    )

    return () => {
      api.interceptors.request.eject(reqInterceptor)
      api.interceptors.response.eject(resInterceptor)
    }
  }, [token, clearSession])

  // ── Save token after login ────────────────────────────────────────────────
  const saveToken = useCallback((rawToken) => {
    try {
      const decoded = jwtDecode(rawToken)
      if (isTokenExpired(decoded)) {
        console.warn('Received already-expired token')
        return
      }
      saveToStorage(rawToken)
      setToken(rawToken)
      setAuthUser(decoded)
    } catch (e) {
      console.error('saveToken error:', e)
    }
  }, [])

  const logout = useCallback(async () => {
    await api.post('/user/admin/logout').catch(() => {})
    clearSession()
  }, [clearSession])

  return (
    <AuthContext.Provider value={{
      authUser,
      token,
      isLoading,
      getData,
      saveToken,
      setAuthUser,
      setToken,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  )
}